// What the install UI is allowed to claim, and when.
//
// The rules here exist because an install prompt is easy to get dishonest:
// a button that does nothing, a nag that will not stop, or an "install" offer
// shown to someone already running the installed app. Each is a small lie and
// each teaches people to ignore the panel.

/** The browser's install event, which only some browsers fire. */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallMode =
  /** A real beforeinstallprompt is in hand — a working button. */
  | 'prompt'
  /** No event, but this browser has a documented manual route: Safari's
   *  File > Add to Dock, or a Chromium browser's own menu. Chrome removed the
   *  fetch-handler requirement for MENU installation while its criteria for
   *  firing beforeinstallprompt stayed stricter, so "no event" does not mean
   *  "not installable" — and hiding every entry point would be wrong. */
  | 'manual'
  /** Already running as an installed app — offer nothing. */
  | 'installed'
  /** A mobile browser: point at the real app, not a home-screen shim. */
  | 'mobile-store'
  /** Nothing honest to offer. */
  | 'none';

export interface Environment {
  hasPromptEvent: boolean;
  standalone: boolean;
  /** navigator.userAgent, read once by the caller. */
  ua: string;
}

const isIOS = (ua: string) => /iPad|iPhone|iPod/.test(ua);
const isAndroid = (ua: string) => /Android/.test(ua);
/** Desktop Safari. Excludes Chrome/Edge, which put "Safari" in their UA too. */
export const isDesktopSafari = (ua: string) =>
  /Safari/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua) && !isIOS(ua) && !isAndroid(ua);

/** Standalone detection. Deliberately only tells us about THIS context: it
 *  can prove the app is installed here, never that it is absent elsewhere. */
export const isStandalone = (): boolean => {
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
    // iOS Safari predates display-mode and uses a non-standard flag.
    return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
};

/** Which Chromium desktop browser this is, for naming its menu accurately.
 *  Order matters: Edge and Brave both contain "Chrome" in their UA. */
export const chromiumFlavour = (ua: string): 'edge' | 'chrome' | null => {
  if (isIOS(ua) || isAndroid(ua)) return null;
  if (/Edg\//.test(ua)) return 'edge';
  if (/Chrome|Chromium/.test(ua)) return 'chrome';
  return null;
};

export const installMode = (env: Environment): InstallMode => {
  if (env.standalone) return 'installed';
  // A real event beats everything: it is the only one-click route.
  if (env.hasPromptEvent) return 'prompt';
  if (isIOS(env.ua) || isAndroid(env.ua)) return 'mobile-store';
  if (isDesktopSafari(env.ua)) return 'manual';
  // A Chromium desktop browser with no event can still install from its own
  // menu. Returning 'none' here hid the feature completely on Chrome — the
  // bug driving dev exposed.
  if (chromiumFlavour(env.ua)) return 'manual';
  return 'none';
};

/** The menu path for this browser, or null when we do not know one. Naming a
 *  menu we have not verified would be worse than saying nothing. */
export const manualInstructions = (ua: string): { label: string; steps: string } | null => {
  if (isDesktopSafari(ua)) {
    return {
      label: 'How to add to Dock',
      steps: 'In Safari, choose File \u2192 Add to Dock, keep the name KaziPeople, then click Add.',
    };
  }
  const flavour = chromiumFlavour(ua);
  if (flavour === 'edge') {
    return {
      label: 'How to install from the Edge menu',
      steps: 'Open the \u2026 menu, choose Apps, then Install this site as an app.',
    };
  }
  if (flavour === 'chrome') {
    return {
      label: 'How to install from the Chrome menu',
      steps: 'Open the \u22ee menu, choose Cast, save and share, then Install page as app.',
    };
  }
  return null;
};

/** The body line for each mode.
 *
 * ⚠️ ONE line for every mode was a lie in two directions. It told someone who
 * had installed nothing to "open your workspace from your Dock or taskbar",
 * and it printed that directly above an Install button (Edge) and above a
 * how-to-install block (Chrome — where the app was ALREADY installed and the
 * browser had simply stopped firing beforeinstallprompt). Both observed in
 * production on 2026-10-05.
 *
 * The 'manual' wording carries the detection limit honestly: from a browser
 * tab we cannot tell "not installed" from "installed, viewed in a tab", so it
 * offers both readings instead of asserting the wrong one.
 *
 * null = this mode's own block already says everything true, or the panel
 * does not render at all.
 */
export const panelBody = (mode: InstallMode): { key: string; text: string } | null => {
  if (mode === 'prompt') {
    return {
      key: 'install.body.prompt',
      text: 'Install it for a launcher in your Dock or taskbar and its own window. Same account for work, conversations and approvals.',
    };
  }
  if (mode === 'manual') {
    return {
      key: 'install.body.manual',
      text: 'Install it from this browser\u2019s menu for a launcher and its own window. If you have already installed it, open it from your Dock or taskbar instead.',
    };
  }
  return null;
};

/** Never render an Install button that cannot install. */
export const canOfferInstallButton = (mode: InstallMode): boolean => mode === 'prompt';

// ---- the dismissal rule -------------------------------------------------

export const DISMISS_KEY = 'kazi-install-dismissed-at';
export const DISMISS_DAYS = 30;

/** Whether the home-screen card may appear. Dismissing it means dismissed —
 *  for 30 days, and storage that throws must not resurrect the nag. */
export const cardAllowed = (dismissedAt: string | null, now = Date.now()): boolean => {
  if (!dismissedAt) return true;
  const then = Number(dismissedAt);
  if (!Number.isFinite(then) || then <= 0) return true; // unparseable: treat as never dismissed
  const days = (now - then) / 86400000;
  // A clock that moved backwards must not unlock the nag early, and must not
  // lock it forever either.
  if (days < 0) return false;
  return days >= DISMISS_DAYS;
};
